/**
 * Central brand & product constants.
 * Single source of truth for tagline, regulators, agents, and pricing tiers.
 * Import from here instead of duplicating string literals across pages.
 */

export const BRAND = {
  name: "Pharmalify",
  domain: "pharmalify.com",
  url: "https://www.pharmalify.com",
  email: {
    contact: "contact@pharmalify.com",
    enterprise: "enterprise@pharmalify.com",
  },
  tagline:
    "Pharmalify Spine — the unified GxP operations backbone for small-to-mid biopharma: Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS), with governed AI Agents and Regulatory Watch built in.",
} as const;

/** Flagship product naming. Use these instead of string literals. */
export const PRODUCT = {
  name: "Pharmalify Spine",

  descriptor: "One connected GxP platform for biopharma quality, laboratory and asset operations",
  pillars: {
    quality: "Quality (eQMS)",
    lab: "QC Lab (LIMS)",
    assets: "Asset Integrity (CMMS)",
  },
  capabilities: {
    ai: "AI Agents",
    regulatory: "Regulatory Watch",
  },
  /** Enterprise CTAs — Spine is contract-based, never self-serve. */
  cta: {
      /** The single site-wide call to action. */
    primary: "Request Walkthrough",
  },
  audience: "small-to-mid biopharma organizations, from emerging biotechs to multi-site teams with hundreds of users",
} as const;

/**
 * Public-copy terms that must not appear on marketing pages (consumer billing
 * language, legacy names, unsupported claims). Enforced by a test.
 */
export const FORBIDDEN_PUBLIC_TERMS: ReadonlyArray<RegExp> = [
  /free trial/i, /waitlist/i, /\bper month\b|\/month\b|\bmonthly plan/i, /Pharma Suite/i,
  /RegulaSight/i, /Signal\s*→\s*Action\s*→\s*Evidence/i, /pre-validated(?! for)/i,
  /\bautonomous\b/i, /SOC 2 certified/i, /Part 11 certified/i, /FDA[- ]certified/i,
  /multi-tenant/i, /Manage Subscription/i, /Plans & Pricing/i, /\bsign up\b/i,
];

/**
 * Authorities monitored by Regulatory Watch.
 * Order is intentional and matches the on-screen visual order.
 */
export const REGULATORS: ReadonlyArray<{ code: string; label: string; region: string }> = [
  { code: "FDA", label: "FDA", region: "United States" },
  { code: "EMA", label: "EMA", region: "European Union" },
  { code: "MHRA", label: "MHRA", region: "United Kingdom" },
  { code: "HC", label: "Health Canada", region: "Canada" },
  { code: "PMDA", label: "PMDA", region: "Japan" },
  { code: "WHO", label: "WHO", region: "Global" },
  { code: "SFDA", label: "SFDA", region: "Saudi Arabia" },
];

export const REGULATOR_COUNT = REGULATORS.length; // 7

/**
 * Comma-separated label list used in marketing prose & meta descriptions.
 * e.g. "FDA, EMA, MHRA, Health Canada, PMDA, WHO and SFDA"
 */
export const REGULATOR_LABELS_PROSE = (() => {
  const labels = REGULATORS.map((r) => r.label);
  return labels.slice(0, -1).join(", ") + " and " + labels[labels.length - 1];
})();

/**
 * Specialized AI agent workspaces currently available.
 * id matches the internal agent_id used by the backend.
 */
export const AGENTS = [
  { id: "investigation", accessId: "investigations_reviewer", notificationId: "investigations", name: "Deviation Management", workspaceName: "Investigations Reviewer", route: "/dashboard/investigations" },
  { id: "inspection", accessId: "inspections_trends", notificationId: "inspections", name: "Inspections & Trends", workspaceName: "Inspections & Trends", route: "/dashboard/inspections" },
  { id: "contract", accessId: "contracts_quality", notificationId: "contracts", name: "Contracts & Quality Agreements", workspaceName: "Contracts & Quality Agreements", route: "/dashboard/contracts" },
  { id: "complaint", accessId: "complaints_management", notificationId: "complaints", name: "Complaints Management", workspaceName: "Complaints Management", route: "/dashboard/complaints" },
  { id: "lab_investigation", accessId: "lab_investigations", notificationId: "lab_investigations", name: "Laboratory Investigations", workspaceName: "Laboratory Investigations", route: "/dashboard/lab-investigations" },
  { id: "supplier_qualification", accessId: "suppliers_qualifications", notificationId: "supplier_qualifications", name: "Suppliers Qualifications", workspaceName: "Suppliers Qualifications", route: "/dashboard/supplier-qualifications" },
  { id: "qms_audit", accessId: "qms_auditor", notificationId: "qms_audits", name: "QMS Builder / Auditor", workspaceName: "QMS Auditor/Builder", route: "/dashboard/qms" },
  { id: "operational_excellence", accessId: "operational_excellence", notificationId: "operational", name: "Change Management", workspaceName: "Change Management", route: "/dashboard/operations" },
];

export const AGENT_COUNT = AGENTS.length;

export const AGENT_ACCESS_OPTIONS = AGENTS.map(({ accessId, workspaceName }) => ({
  id: accessId,
  label: workspaceName,
}));

export const AGENT_NOTIFICATION_LABELS = Object.fromEntries(
  AGENTS.map(({ notificationId, workspaceName }) => [notificationId, workspaceName]),
) as Record<string, string>;

export const AGENT_LIBRARY_OPTIONS = [
  { value: "global", label: "Global" },
  ...AGENT_ACCESS_OPTIONS.map(({ id, label }) => ({ value: id, label })),
];

export const PLAN_IDS = ["startups", "provider", "corporate"] as const;
export type PlanId = (typeof PLAN_IDS)[number];
